import { useEffect, useId, useRef, type ReactNode } from "react";
import { Card, CardBody, CardTitle } from "./Card";
import { typography } from "./Typography/styles";
import { Close } from "./icons";
import { cn } from "packages/dsaui/src/utils";

export interface ModalProps {
  actions: ReactNode;
  className?: string;
  closeLabel: string;
  description?: ReactNode;
  onClose: () => void;
  title: ReactNode;
}

export const Modal = ({
  actions,
  className,
  closeLabel,
  description,
  onClose,
  title,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const descriptionId = useId();
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const closeOnBackgroundClick = (event: MouseEvent) => {
      if (event.target === dialog) dialog.close();
    };
    dialog.addEventListener("click", closeOnBackgroundClick);
    if (!dialog.open) dialog.showModal();

    return () => {
      dialog.removeEventListener("click", closeOnBackgroundClick);
      if (dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      aria-describedby={description ? descriptionId : undefined}
      aria-labelledby={titleId}
      className={cn(
        "m-auto w-9/10 max-w-md border-0 bg-transparent p-0 backdrop:bg-black/40",
        className,
      )}
      onClose={() => {
        // Strict Mode can reopen the dialog before a queued close event fires.
        if (!dialogRef.current?.open) onClose();
      }}
      ref={dialogRef}
    >
      <Card className="relative w-full max-w-md p-8">
        <CardBody className="gap-6 pt-3">
          <button
            aria-label={closeLabel}
            className="focus-visible:ring-dsa-red absolute top-5 right-5 cursor-pointer border-0 bg-transparent p-1 text-gray-500 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            onClick={() => dialogRef.current?.close()}
            type="button"
          >
            <Close className="block size-5" />
          </button>

          <div className="space-y-2 px-2 text-center">
            <CardTitle className="mx-auto max-w-sm" id={titleId}>
              {title}
            </CardTitle>
            {description ? (
              <p
                className={cn("m-0", typography.formSubtitle.className)}
                id={descriptionId}
              >
                {description}
              </p>
            ) : null}
          </div>

          <div className="grid gap-3">{actions}</div>
        </CardBody>
      </Card>
    </dialog>
  );
};

export default Modal;
